import type { ItemId } from './items.js';
import type { SkillId } from './skills.js';
import type { ToolLine } from './tools.js';

/**
 * Obstacles are unlocked per character (like a quest unlock), not for the whole world.
 * The map places them; this table says what clearing one takes.
 */
export type ObstacleKind = 'rockslide' | 'bramble' | 'bridge' | 'stones';

export interface ObstacleDef {
  name: string;
  verb: string;
  doing: string;
  requires?: { skill: SkillId; level: number };
  consumes?: Partial<Record<ItemId, number>>;
  /** A tool from this line, at least this tier, usable at your level. */
  needsTool?: [ToolLine, number];
  rewards: Partial<Record<SkillId, number>>;
  /** Tool shown in the player's hand while clearing. */
  tool?: 'axe' | 'pick' | 'hammer';
  ticks: number;
  fail: string;
  done: string;
}

export const OBSTACLES: Record<ObstacleKind, ObstacleDef> = {
  rockslide: {
    name: 'Rockslide', verb: 'Clear', doing: 'Clearing the rockslide', requires: { skill: 'mining', level: 12 }, needsTool: ['pick', 1], rewards: { mining: 120, exploration: 150 }, tool: 'pick', ticks: 4,
    fail: 'Loose boulders block the pass. You need Mining 12 and a bronze pickaxe or better to clear them.', done: 'You pry the boulders loose. The pass to Rustcliff is open.',
  },
  bramble: {
    name: 'Bramble thicket', verb: 'Cut', doing: 'Cutting through the bramble', requires: { skill: 'forestry', level: 25 }, needsTool: ['axe', 2], rewards: { forestry: 300, exploration: 250 }, tool: 'axe', ticks: 4,
    fail: 'The thorns are as thick as wrists. You need Forestry 25 and an iron hatchet or better to cut through.', done: 'You hack a path through the bramble. Hollow Grove lies beyond.',
  },
  bridge: {
    name: 'Broken bridge', verb: 'Repair', doing: 'Repairing the bridge', consumes: { pine_log: 8, iron_bar: 3 }, needsTool: ['hammer', 0], rewards: { smithing: 150, exploration: 200 }, tool: 'hammer', ticks: 4,
    fail: 'Half the planks are gone. Repairing it takes a hammer, 8 pine logs and 3 iron bars.', done: 'You nail new planks into place. The bridge to Ashen Ridge holds.',
  },
  stones: {
    name: 'Stepping stones', verb: 'Cross', doing: 'Testing the stones', requires: { skill: 'exploration', level: 20 }, rewards: { exploration: 300 }, ticks: 4,
    fail: 'The stones are slick and far apart. You need Exploration 20 to find a safe way across.', done: 'You find the rhythm of the stones and cross toward Stillwater Cove.',
  },
};
export const isObstacleKind = (v: unknown): v is ObstacleKind => typeof v === 'string' && Object.prototype.hasOwnProperty.call(OBSTACLES, v);
